import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import {
  fetchPublicLegalDocument,
  formatLegalPublishedAt,
  initialLegalDocument,
  normalizeLegalMarkdown,
  type PublicLegalDocumentDto,
  type PublicLegalDocumentCode,
} from '@/lib/legal';

interface LegalDocumentViewProps {
  code: PublicLegalDocumentCode;
  fallbackTitle: string;
}

export function LegalDocumentView({ code, fallbackTitle }: LegalDocumentViewProps) {
  // O documento já vem no HTML gerado no build: a página nasce pronta, legível
  // por buscadores, e o React assume sem trocar o conteúdo. A busca na API só
  // atualiza por cima caso haja versão mais nova publicada depois do build.
  const inicial = initialLegalDocument(code);
  const [doc, setDoc] = useState<PublicLegalDocumentDto | null>(inicial);
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>(inicial ? 'ready' : 'loading');

  useEffect(() => {
    let cancelled = false;
    fetchPublicLegalDocument(code)
      .then((data) => {
        if (!cancelled) {
          setDoc(data);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled && !inicial) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div className="shell legal-doc">
      <Link to="/" className="legal-doc-back">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Voltar ao início
      </Link>

      <article className="legal-doc-card">
        {status === 'loading' ? (
          <p className="legal-doc-status">Carregando…</p>
        ) : null}

        {status === 'error' || (status === 'ready' && !doc) ? (
          <p className="legal-doc-status">
            Não foi possível carregar o documento agora. Tente de novo em instantes.
          </p>
        ) : null}

        {status === 'ready' && doc ? (
          <>
            <h1 className="legal-doc-title">{doc.title || fallbackTitle}</h1>
            <p className="legal-doc-meta">
              Versão {doc.version} · Atualizado em {formatLegalPublishedAt(doc.published_at)}
            </p>
            <div className="legal-markdown">
              {/* O título da página já é o <h1>; um "# Título" no texto viraria um segundo. */}
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ h1: 'h2' }}>
                {normalizeLegalMarkdown(doc.content_markdown)}
              </ReactMarkdown>
            </div>
          </>
        ) : null}
      </article>
    </div>
  );
}
